// Per-browser device preferences (mic / speaker / camera + audio
// processing), persisted to localStorage. Read by the capture mixer
// (solo recorder) and the hosted call view. Selection is a client
// concern — nothing about it reaches the server.

export interface DevicePrefs {
  /** mediaDeviceId of the preferred microphone ("" = system default). */
  micId: string;
  /** mediaDeviceId of the preferred speaker / output. */
  speakerId: string;
  /** mediaDeviceId of the preferred camera. */
  cameraId: string;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
}

const KEY = "mm-devices";

const DEFAULTS: DevicePrefs = {
  micId: "",
  speakerId: "",
  cameraId: "",
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

export function loadDevicePrefs(): DevicePrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<DevicePrefs>) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveDevicePrefs(patch: Partial<DevicePrefs>): DevicePrefs {
  const next = { ...loadDevicePrefs(), ...patch };
  localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export interface DeviceList {
  mics: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  cameras: MediaDeviceInfo[];
}

/** Enumerate inputs/outputs. Labels are blank until permission is
 *  granted once, so callers typically getUserMedia first. */
export async function listDevices(): Promise<DeviceList> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return {
    mics: devices.filter((d) => d.kind === "audioinput"),
    speakers: devices.filter((d) => d.kind === "audiooutput"),
    cameras: devices.filter((d) => d.kind === "videoinput"),
  };
}

/** Audio capture constraints from the saved prefs (mic + processing). */
export function micConstraints(
  overrideMicId?: string,
  prefs: DevicePrefs = loadDevicePrefs(),
): MediaTrackConstraints {
  const id = overrideMicId ?? (prefs.micId || undefined);
  return {
    deviceId: id ? { exact: id } : undefined,
    echoCancellation: prefs.echoCancellation,
    noiseSuppression: prefs.noiseSuppression,
    autoGainControl: prefs.autoGainControl,
  };
}

/** Route a media element to the preferred speaker, where supported
 *  (setSinkId is Chromium-only and a no-op elsewhere). */
export async function applySpeaker(
  el: HTMLMediaElement,
  speakerId = loadDevicePrefs().speakerId,
): Promise<void> {
  if (!speakerId) return;
  const sinkable = el as HTMLMediaElement & {
    setSinkId?: (id: string) => Promise<void>;
  };
  if (typeof sinkable.setSinkId !== "function") return;
  try {
    await sinkable.setSinkId(speakerId);
  } catch {
    // Device gone or unsupported — fall back to the default sink.
  }
}
